import React, { FunctionComponent, useEffect, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import { createPortal } from 'react-dom'
import 'react-datepicker/dist/react-datepicker.min.css'
import { FieldInputProps, FieldRenderProps } from 'react-final-form'
import { createIntl, createIntlCache, useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'

import { match } from '@formatjs/intl-localematcher'
import { isValid, parse, parseISO, toDate } from 'date-fns'
import * as allLocales from 'date-fns/locale'
import { get, isEmpty } from 'lodash-es'
import styled, { createGlobalStyle, CSSObject } from 'styled-components'
import { useTheme } from 'styled-components/native'

import { BaseInput } from './BaseInput'
import { useFocusState } from '../../hooks/useFocusState'
import { getCommonStyles, getInputFieldStyles } from '../../styles/commonStyles'
import { moderatRegular } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'

const stringLocaleToDateLocaleMapper = {
  'en-US': allLocales.enUS,
  'en-GB': allLocales.enGB,
  'en-CA': allLocales.enCA,
  'fr-CA': allLocales.frCA,
  'en-AU': allLocales.enAU,
  'en-IE': allLocales.enIE,
  de: allLocales.de,
  'pt-BR': allLocales.ptBR,
  pt: allLocales.pt,
  pl: allLocales.pl,
  ja: allLocales.ja,
  ko: allLocales.ko,
  nl: allLocales.nl,
  bg: allLocales.bg,
  cs: allLocales.cs,
  th: allLocales.th,
  hu: allLocales.hu,
  id: allLocales.id,
  ro: allLocales.ro,
  tr: allLocales.tr,
  vi: allLocales.vi,
  hi: allLocales.hi,
  it: allLocales.it,
  ms: allLocales.ms,
  fr: allLocales.fr,
  es: allLocales.es,
  'zh-CN': allLocales.zhCN,
  'zh-HK': allLocales.zhHK,
  'zh-TW': allLocales.zhTW,
}

const stringLocaleToDateLocale = (locale?: string): Locale => {
  if (!locale) return stringLocaleToDateLocaleMapper['en-US']
  const key = match([locale], Object.keys(stringLocaleToDateLocaleMapper), 'en-US')
  return (
    stringLocaleToDateLocaleMapper[key as keyof typeof stringLocaleToDateLocaleMapper] ??
    stringLocaleToDateLocaleMapper['en-US']
  )
}

const bodyDefault = { fontSize: 16, lineHeight: 24, ...moderatRegular }

const DatePickerContainer = styled(DatePicker)<{
  error?: boolean
  borderRadius?: number
  customHeight?: number
  textInputStyles?: CSSObject
  colors: ThemeType['colors']
}>(({ error, borderRadius, customHeight, textInputStyles, colors }) => {
  const defaultBorderRadius = textInputStyles?.borderRadius ?? 4
  const defaultBorderWidth = textInputStyles?.borderWidth ?? 1
  const defaultPaddingH = textInputStyles?.paddingHorizontal ?? 15
  const defaultPaddingV = textInputStyles?.paddingVertical ?? 11
  const borderColor = (textInputStyles?.borderColor as string) ?? colors.inputOutlineDefault
  const backgroundColor = (textInputStyles?.backgroundColor as string) ?? colors.inputBackgroundDefault
  const textColor = (textInputStyles?.color as string) ?? colors.inputTextDefault
  return {
    ...bodyDefault,
    boxSizing: 'border-box',
    lineHeight: `${bodyDefault.lineHeight}px`,
    width: '100%',
    marginBottom: '0px',
    color: textColor,
    border: `${defaultBorderWidth}px solid ${borderColor}`,
    borderRadius: `${borderRadius ?? defaultBorderRadius}px`,
    padding: `${defaultPaddingV}px ${defaultPaddingH}px`,
    backgroundColor,
    height: customHeight ? `${customHeight}px` : '48px',
    outline: 'none',
    ...(error && {
      borderColor: colors.inputOutlineError,
    }),
    '&::placeholder': {
      color: `${colors.inputTextPlaceholder}80`,
      opacity: 1,
    },
  }
})

const DatePickerInputWrapper = styled.div({
  position: 'relative',
  display: 'block',
})

const RightIconContainer = styled.div({
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const ClearIconButton = styled.button({
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
})

const ReadOnlyDateContainer = styled.div<{
  borderRadius?: number
  customHeight?: number
  textInputStyles?: CSSObject
  colors: ThemeType['colors']
}>(({ borderRadius, customHeight, textInputStyles, colors }) => {
  const defaultBorderRadius = textInputStyles?.borderRadius ?? 4
  const defaultPaddingH = textInputStyles?.paddingHorizontal ?? 15
  const defaultPaddingV = textInputStyles?.paddingVertical ?? 11
  const backgroundColor = (textInputStyles?.backgroundColor as string) ?? colors.inputBackgroundInactive
  const textColor = (textInputStyles?.color as string) ?? colors.inputTextInactive

  return {
    ...bodyDefault,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: '100%',
    marginBottom: '0px',
    lineHeight: `${bodyDefault.lineHeight}px`,
    height: customHeight ? `${customHeight}px` : 'auto',
    minHeight: '48px',
    borderRadius: `${borderRadius ?? defaultBorderRadius}px`,
    padding: `${defaultPaddingV}px ${defaultPaddingH}px`,
    color: textColor,
    backgroundColor,
  }
})

export const DatePickerGlobalStyles = createGlobalStyle<{ zIndex?: number; theme: ThemeType }>`
  div.react-datepicker-wrapper {
    display: block;
    .react-datepicker__input-container {
      display: block;
      font-size: 16px;
      .react-datepicker__close-icon {
        padding: 0 16px 0 0;
        &:after {
          background-color: ${({ theme }) => theme.colors.iconDefault};
        }
        &:focus:after {
          box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.backgroundPrimary},
            0 0 0 6px ${({ theme }) => theme.colors.borderFocus};
        }
      }
    }
  }
  div.react-datepicker-popper {
    z-index: ${({ zIndex }) => zIndex ?? 220};
    .react-datepicker {
      font-size: 1em;
      background-color: ${({ theme }) => theme.colors.backgroundPrimary};
      border-color: ${({ theme }) => theme.colors.borderDefault};
    }
    .react-datepicker__header {
      padding-top: 0.8em;
      background-color: ${({ theme }) => theme.colors.backgroundPrimary};
      border-bottom-color: ${({ theme }) => theme.colors.borderDefault};
    }
    .react-datepicker__header__dropdown {
      display: flex;
      margin: 8px 0;
      padding: 0 10px;
      gap: 12px;
    }
    .react-datepicker__month {
      margin: 0.4em 1em;
    }
    .react-datepicker__month-dropdown-container {
      margin: 0;
      flex-grow: 1;
    }
    .react-datepicker__year-dropdown-container {
      margin: 0;
      flex-grow: 1;
    }
    .react-datepicker__year-dropdown-container select, .react-datepicker__month-dropdown-container select {
      font-family: 'moderat', sans-serif;
      color: ${({ theme }) => theme.colors.inputTextDefault};
      background-color: ${({ theme }) => theme.colors.inputBackgroundDefault};
      border-color: ${({ theme }) => theme.colors.inputOutlineDefault};
      border-radius: 3px;
      height: 46px;
      caret-color: ${({ theme }) => theme.colors.primaryButtonBackgroundDefault};
      padding: 0 35px 0 15px;
      outline: none;
      width: 100%;
      appearance: none;
      background-repeat: no-repeat;
      background-position: center right 10px;
      background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMTRweCIgaGVpZ2h0PSI4cHgiIHZpZXdCb3g9IjAgMCAxNCA4IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPgogICAgPHRpdGxlPmNoZXZEb3duIENvcHk8L3RpdGxlPgogICAgPGcgaWQ9ImNoZXZEb3duLUNvcHkiIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxwYXRoIGQ9Ik05LjIyODczOTgxLDEwLjkwOTA3NSBDOS4wMjE5NTQ2NywxMC45MDkwNzUgOC44MTUxNjk1MywxMC44MjY1NTE1IDguNjU3OTI2NjcsMTAuNjYwMzczOSBMMy4yNzI4OTcwNSw1LjAwODA3NzE1IEMyLjk1NzMzNDMyLDQuNjc2ODUyNTYgMi45NTczMzQzMiw0LjE0MTAxNDgyIDMuMjcyODk3MDUsMy44MDk3OTAyMyBMOC42NTc5MjY2NywtMS44NDI1MDY1NiBDOC45NzM0ODk0LC0yLjE3MzczMTE1IDkuNDgzOTkwMjEsLTIuMTczNzMxMTUgOS43OTk1NTI5NSwtMS44NDI1MDY1NiBDMTAuMTE1MTE1NywtMS41MTEyODE5NiAxMC4xMTUxMTU3LC0wLjk3NTQ0NDIyOSA5Ljc5OTU1Mjk1LC0wLjY0NDIxOTYzNyBMNC45ODY0MTM0OCw0LjQwODkzMzY5IEw5Ljc5OTU1Mjk1LDkuNDYyMDg3MDIgQzEwLjExNTExNTcsOS43OTMzMTE2MSAxMC4xMTUxMTU3LDEwLjMyOTE0OTMgOS43OTk1NTI5NSwxMC42NjAzNzM5IEM5LjY0MjMxMDA4LDEwLjgyNjU1MTUgOS40MzU1MjQ5NSwxMC45MDkwNzUgOS4yMjg3Mzk4MSwxMC45MDkwNzUiIGlkPSJGaWxsLTQ3IiBmaWxsPSIjNjg3MDc4IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSg2LjUzNjIyNSwgNC40MDkwNzUpIHJvdGF0ZSgtOTAuMDAwMDAwKSB0cmFuc2xhdGUoLTYuNTM2MjI1LCAtNC40MDkwNzUpICI+PC9wYXRoPgogICAgPC9nPgo8L3N2Zz4=');
    }
    .react-datepicker__month-select, .react-datepicker__year-select {
      &:focus {
        box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.inputOutlineFocus}
      }
    }
    .react-datepicker__day-name {
      width: 1.9em;
      line-height: 1.9em;
      margin: 0.166em;
      color: ${({ theme }) => theme.colors.textSecondary};
    }
    .react-datepicker__day {
      width: 1.9em;
      line-height: 1.9em;
      margin: 0.166em;
      color: ${({ theme }) => theme.colors.textPrimary};
      &:hover {
        background-color: ${({ theme }) => theme.colors.backgroundSecondary};
      }
      &.react-datepicker__day--disabled {
        color: ${({ theme }) => theme.colors.textInactive};
        &:hover {
          background-color: transparent;
        }
      }
      &.react-datepicker__day--outside-month {
        color: ${({ theme }) => theme.colors.textInactive};
      }
      &.react-datepicker__day--keyboard-selected, &.react-datepicker__day--selected {
        background-color: ${({ theme }) => theme.colors.primaryButtonBackgroundDefault};
        color: ${({ theme }) => theme.colors.primaryButtonTextActive};
        &:hover, &:focus {
          background-color: ${({ theme }) => theme.colors.primaryButtonBackgroundHover};
        }
        &:focus {
          outline: 0;
          box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.backgroundPrimary},
            0 0 0 6px ${({ theme }) => theme.colors.borderFocus};
        }
      }
    }
    .react-datepicker__current-month {
      font-size: 1em;
      color: ${({ theme }) => theme.colors.textPrimary};
    }
    .react-datepicker__navigation {
      top: 1em;
      line-height: 1.7em;
      border: 0.45em solid transparent;
      border-radius: 2px;

      &:focus {
        outline: 0;
        box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.borderFocus};
      }
    }
    .react-datepicker__navigation-icon::before {
      border-color: ${({ theme }) => theme.colors.iconDefault}
    }
    .react-datepicker__navigation-icon--previous {
      margin-left: 22px;
      margin-bottom: 10px;
    }
    .react-datepicker__navigation-icon--next {
      margin-right: 22px;
      margin-bottom: 10px;
    }
    div.react-datepicker__triangle {
      left: -10px !important;
      &::before {
        border-bottom-color: ${({ theme }) => theme.colors.borderDefault};
      }
      &::after {
        border-bottom-color: ${({ theme }) => theme.colors.backgroundPrimary};
      }
    }
  }
`

const cache = createIntlCache()

export const DateField: FunctionComponent<DateInputFieldProps> = ({
  appendFormat = true,
  borderRadius,
  createInPortal = true,
  disabled,
  dropdownMode = 'select',
  error,
  input,
  isClearable = true,
  label,
  maxDate,
  minDate,
  readOnly,
  showMonthDropdown = true,
  showYearDropdown = true,
  submitError,
  locale,
  onRawChange = () => {},
  inputStyle,
  zIndex,
  placeholder,
  customHeight,
  styledReadOnly,
  syncExternalChanges = false,
  rightIcon,
  clearIcon,
  required,
  ...props
}) => {
  const intl = useIntl()
  const theme = useTheme()
  const { colors, fontFamilyMetadata, componentStyles } = theme
  const commonStyles = getCommonStyles(colors, fontFamilyMetadata, componentStyles)
  const { isFocused, focusProps } = useFocusState({ onFocus: input.onFocus, onBlur: input.onBlur })
  const [value, setValue] = useState<Date | null | undefined>(null)
  const [datePlaceholder, setDatePlaceholder] = useState<string>(placeholder || '__/__/____')
  const [dateFormat, setDateFormat] = useState<string>('MM/dd/yyyy')

  // This will set the locale for the date picker library
  useEffect(() => {
    const dateLocale = stringLocaleToDateLocale(locale)
    locale && registerLocale(locale, dateLocale)
    const dateFormatStr = dateLocale.formatLong?.date({ width: 'short' })
    setDateFormat(dateFormatStr)
    if (!placeholder) {
      setDatePlaceholder(dateFormatStr.replace(/[a-zA-Z]/g, '_'))
    }
  }, [locale, placeholder])

  // Sync internal state with external form state changes (opt-in for forms that programmatically update date fields)
  useEffect(() => {
    if (!syncExternalChanges) return

    const inputValue = get(input, 'value')
    if (inputValue) {
      // Try parsing as ISO format first (YYYY-MM-DD), then fall back to locale-specific format
      const isoDate = parseISO(inputValue)
      const parsedDate = isoDate && isValid(isoDate) ? isoDate : parse(inputValue, dateFormat, new Date())
      if (isValid(parsedDate) && (!value || parsedDate.getTime() !== value.getTime())) {
        setValue(parsedDate)
      }
    } else if (value !== null) {
      setValue(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value, dateFormat, value, syncExternalChanges])

  const handleChange = (date: Date | null) => {
    if (date === null) {
      setValue(null)
      input.onChange('')
      return
    }
    let intlOverride = intl
    if (!isEmpty(locale)) {
      intlOverride = createIntl({ locale }, cache)
    }
    if (isValid(date)) {
      const formattedDate = intlOverride.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' })
      setValue(date)
      input.onChange(formattedDate)
    }
  }

  // Modify selected days label to indicate that it is selected
  const calendarOpen = () => {
    window.requestAnimationFrame(() => {
      const monthSelect = document.querySelector('.react-datepicker__month-select')
      const yearSelect = document.querySelector('.react-datepicker__year-select')
      monthSelect && monthSelect.setAttribute('aria-label', 'Month')
      yearSelect && yearSelect.setAttribute('aria-label', 'Year')
      const selectedDateElement = document.querySelector('[tabindex="0"].react-datepicker__day')
      if (selectedDateElement) {
        const newSelectedDateLabel = selectedDateElement.getAttribute('aria-label')?.replace('Choose', 'Chosen')
        selectedDateElement.setAttribute('aria-label', `${newSelectedDateLabel}`)
      }
    })
  }

  const inputValue = get(input, 'value')
  // Try parsing as ISO format first (YYYY-MM-DD), then fall back to locale-specific format
  const isoDate = inputValue ? parseISO(inputValue) : null
  const parsedDate = isoDate && isValid(isoDate) ? isoDate : parse(inputValue, dateFormat, new Date())
  const inputValueAsDateOrNull = isValid(parsedDate) ? parsedDate : null

  const commitYearMonthChange = ({ year, month }: { year: number; month: number }) => {
    const currentSelected = value || inputValueAsDateOrNull
    if (!currentSelected || !isValid(currentSelected)) return
    const day = currentSelected.getDate()
    const maxDay = new Date(year, month + 1, 0).getDate()
    const newDate = new Date(year, month, Math.min(day, maxDay))
    if (minDate && newDate < toDate(minDate)) return
    if (maxDate && newDate > toDate(maxDate)) return
    handleChange(newDate)
  }

  const handleYearChange = (newDate: Date) => {
    const currentSelected = value || inputValueAsDateOrNull
    if (!currentSelected || !isValid(currentSelected)) return
    commitYearMonthChange({ year: newDate.getFullYear(), month: currentSelected.getMonth() })
  }

  const handleMonthChange = (newDate: Date) => {
    const currentSelected = value || inputValueAsDateOrNull
    if (!currentSelected || !isValid(currentSelected)) return
    commitYearMonthChange({ year: newDate.getFullYear(), month: newDate.getMonth() })
  }

  const errorMessage = error || submitError
  const errorMessageId = errorMessage ? `${input.name}-error-message` : undefined
  const formattedLabel = appendFormat ? `${label} (${dateFormat.toUpperCase()})` : label

  const textInputStyles = getInputFieldStyles({
    colors,
    fontFamilyMetadata,
    componentStyles,
    active: isFocused,
    error: !!errorMessage,
  }) as CSSObject

  const renderReadOnlyContent = () => {
    if (styledReadOnly) {
      return (
        <ReadOnlyDateContainer
          borderRadius={borderRadius}
          customHeight={customHeight}
          textInputStyles={commonStyles.textInputInactive as CSSObject}
          colors={colors}
        >
          {input.value}
        </ReadOnlyDateContainer>
      )
    }
    return <p style={{ margin: 0 }}>{input.value}</p>
  }

  return (
    <BaseInput
      error={errorMessage}
      label={formattedLabel}
      name={input.name}
      readOnly={readOnly}
      style={inputStyle}
      required={required}
    >
      {readOnly ? (
        renderReadOnlyContent()
      ) : (
        <>
          <DatePickerInputWrapper>
            <DatePickerContainer
              ariaLabelClose={intl.formatMessage({
                defaultMessage: 'Clear',
                description: 'Accessibility label for the clear button in the date picker',
              })}
              borderRadius={borderRadius}
              customHeight={customHeight}
              colors={colors}
              textInputStyles={textInputStyles}
              {...(errorMessageId && { ariaDescribedBy: errorMessageId })}
              autoComplete={'off'}
              customInput={
                <input
                  type='text'
                  style={rightIcon ? { paddingRight: 42 } : undefined} // right offset (12) + icon size (24) + gap (6)
                  required={required}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      // Focus the date picker to open it
                      const datePickerInput = event.target as HTMLInputElement
                      datePickerInput.click()
                    }
                  }}
                />
              }
              disabled={readOnly ? true : disabled}
              dropdownMode={dropdownMode}
              error={!!errorMessage}
              id={`DateField-${input.name}`}
              isClearable={clearIcon ? false : readOnly ? false : isClearable}
              name={input.name}
              maxDate={maxDate ? toDate(maxDate) : undefined}
              minDate={minDate ? toDate(minDate) : undefined}
              onBlur={focusProps.onBlur}
              onCalendarOpen={calendarOpen}
              onChange={handleChange}
              onYearChange={handleYearChange}
              onMonthChange={handleMonthChange}
              onFocus={focusProps.onFocus}
              placeholderText={datePlaceholder}
              popperContainer={({ children }: { children: React.ReactNode }) => {
                return createInPortal ? createPortal(children, document.body) : <>{children}</>
              }}
              showMonthDropdown={showMonthDropdown}
              showYearDropdown={showYearDropdown}
              selected={value || inputValueAsDateOrNull}
              locale={locale}
              dateFormat={'P'}
              onChangeRaw={(e) => onRawChange({ inputString: (e?.target as HTMLInputElement)?.value, dateFormat })}
              {...props}
            />
            {rightIcon && !inputValueAsDateOrNull && <RightIconContainer>{rightIcon}</RightIconContainer>}
            {clearIcon && !!inputValueAsDateOrNull && (
              <ClearIconButton
                type='button'
                aria-label={intl.formatMessage({
                  defaultMessage: 'Clear date',
                  description: 'Accessibility label for the clear date button in the date picker',
                })}
                onClick={() => handleChange(null)}
              >
                {clearIcon}
              </ClearIconButton>
            )}
          </DatePickerInputWrapper>
          <DatePickerGlobalStyles zIndex={zIndex} theme={theme} />
        </>
      )}
    </BaseInput>
  )
}

export const DateFieldRFF: FunctionComponent<FieldRenderProps<string>> = ({
  appendFormat,
  disabled,
  dropdownMode,
  input,
  isClearable,
  label,
  maxDate,
  meta: { error, submitError, touched },
  minDate,
  readOnly,
  showMonthDropdown,
  showYearDropdown,
  locale,
  ...props
}) => {
  return (
    <DateField
      appendFormat={appendFormat}
      disabled={disabled}
      dropdownMode={dropdownMode}
      error={touched && error}
      input={input}
      isClearable={isClearable}
      label={label}
      maxDate={maxDate}
      minDate={minDate}
      readOnly={readOnly}
      showMonthDropdown={showMonthDropdown}
      showYearDropdown={showYearDropdown}
      submitError={submitError}
      locale={locale}
      {...props}
    />
  )
}

export interface DateInputFieldProps {
  appendFormat?: boolean
  borderRadius?: number
  createInPortal?: boolean
  disabled?: boolean
  dropdownMode?: 'scroll' | 'select'
  error: string
  input: FieldInputProps<any, HTMLElement>
  isClearable?: boolean
  label: string
  maxDate?: Date | null
  minDate?: Date | null
  readOnly: boolean
  showMonthDropdown?: boolean
  showYearDropdown?: boolean
  submitError: string
  locale: string
  onRawChange?: ({ inputString, dateFormat }: { inputString: string; dateFormat: string }) => void
  onSelect?: (date: Date) => void
  open?: boolean
  inputStyle?: ViewStyle
  zIndex?: number
  preventOpenOnFocus?: boolean
  placeholder?: string
  customHeight?: number
  styledReadOnly?: boolean
  syncExternalChanges?: boolean
  rightIcon?: React.ReactNode
  clearIcon?: React.ReactNode
  required?: boolean
}
